import React, { useState } from 'react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import { adminAPI } from '../../lib/api';
import { X } from '@phosphor-icons/react';
import ManagerSelectField from './ManagerSelectField';

// Minimal modal — no Shadcn Dialog dependency to keep the bundle lean.
// Reuses the cream/green theme used throughout AdminPage.

// Shared input class for native datetime + number/text inputs. Sized to
// avoid iOS Safari clipping on `type="datetime-local"` and to meet the
// 44px touch-target minimum on mobile.
const INPUT_CLS =
  "w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-[#e3dcd1] bg-white " +
  "text-[15px] appearance-none focus:outline-none focus:border-[#015e3f]/40";

export default function CreateSessionDialog({ open, onClose, onCreated, embedded = false }) {
  const [name, setName] = useState("Men's Night");
  const [gender, setGender] = useState('mens');
  const [eventSlug, setEventSlug] = useState('mens-night');
  const [numCourts, setNumCourts] = useState(3);
  const [playersPerCourt, setPlayersPerCourt] = useState(4);
  const [weeksPlanned, setWeeksPlanned] = useState(8);
  const [gamesPerWeek, setGamesPerWeek] = useState(36);
  const [startsAt, setStartsAt] = useState('');
  const [registrationClosesAt, setRegistrationClosesAt] = useState('');
  const [allowSelfRegister, setAllowSelfRegister] = useState(false);
  const [managerProfileId, setManagerProfileId] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!embedded && !open) return null;

  const maxPlayers = numCourts * playersPerCourt;

  const handleGenderChange = (g) => {
    setGender(g);
    if (g === 'mens') {
      setEventSlug('mens-night');
      setName("Men's Night");
      setGamesPerWeek(36);
    } else {
      setEventSlug('ladies-night');
      setName("Ladies Night");
      setGamesPerWeek(24);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const r = await eventAPI.createSession({
        event_slug: eventSlug,
        name: name.trim(),
        gender,
        num_courts: Number(numCourts),
        players_per_court: Number(playersPerCourt),
        weeks_planned: Number(weeksPlanned),
        games_per_week: Number(gamesPerWeek),
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        registration_closes_at: registrationClosesAt ? new Date(registrationClosesAt).toISOString() : null,
        allow_self_register: allowSelfRegister,
      });
      const created = r.data;
      if (managerProfileId && created?.id) {
        try {
          await adminAPI.assignEventManager('event_session', created.id, managerProfileId);
        } catch (e) {
          const detail = e.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : (detail?.message || 'Assignment failed.');
          toast.warning(`Event created, but Event Manager could not be assigned: ${msg}. You can retry from Manage.`);
        }
      }
      toast.success('Session created');
      onCreated?.(created);
      if (!embedded) onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create session');
    } finally {
      setSaving(false);
    }
  };

  const body = (
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="w-full bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5 space-y-4"
        data-testid="create-session-dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-2xl text-[#013523]">New Session</h3>
            <p className="text-xs text-[#6a766e] mt-0.5">Court Movement League</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="create-session-close"
            className={`text-[#6a766e] hover:text-[#013523] ${embedded ? 'hidden' : ''}`}
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2" data-testid="create-session-gender">
          {['mens', 'womens'].map((g) => (
            <button
              key={g}
              type="button"
              data-testid={`create-session-gender-${g}`}
              onClick={() => handleGenderChange(g)}
              className={`px-3 py-2 rounded-lg text-sm border transition ${
                gender === g
                  ? 'bg-[#015e3f] text-white border-[#015e3f]'
                  : 'bg-[#fbfaf8] text-[#013523] border-[#e3dcd1]'
              }`}
            >
              {g === 'mens' ? "Men's Night" : 'Ladies Night'}
            </button>
          ))}
        </div>

        <Field label="Session name">
          <input
            data-testid="create-session-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${INPUT_CLS} text-[#013523]`}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Courts">
            <input
              data-testid="create-session-courts"
              type="number" min={1} max={10}
              value={numCourts}
              onChange={(e) => setNumCourts(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Players / court">
            <input
              data-testid="create-session-per-court"
              type="number" min={2} max={8}
              value={playersPerCourt}
              onChange={(e) => setPlayersPerCourt(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Weeks planned">
            <input
              data-testid="create-session-weeks"
              type="number" min={1} max={26}
              value={weeksPlanned}
              onChange={(e) => setWeeksPlanned(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Games / week">
            <input
              data-testid="create-session-games"
              type="number" min={1} max={200}
              value={gamesPerWeek}
              onChange={(e) => setGamesPerWeek(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
        </div>

        {/* Date inputs need their own breakpoint — at <640px the native
            datetime-local widget plus its icon doesn't fit in half-column
            and the two fields visually collide. Stack on mobile, side-by-
            side on sm+. */}
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Starts at (optional)">
            <input
              data-testid="create-session-starts-at"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Registration closes (optional)">
            <input
              data-testid="create-session-closes-at"
              type="datetime-local"
              value={registrationClosesAt}
              onChange={(e) => setRegistrationClosesAt(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
        </div>

        <label className="flex items-start gap-2 cursor-pointer" data-testid="create-session-allow-self-register">
          <input
            type="checkbox"
            checked={allowSelfRegister}
            onChange={(e) => setAllowSelfRegister(e.target.checked)}
            className="mt-0.5 accent-[#015e3f]"
          />
          <div className="text-xs">
            <div className="font-medium text-[#013523]">Allow self-registration</div>
            <div className="text-[#6a766e]">
              When OFF, only admins can add players. When ON, approved members can self-register while status is open.
            </div>
          </div>
        </label>

        <div className="text-xs text-[#6a766e]">
          Max players: <span className="font-medium text-[#013523]">{maxPlayers}</span>
        </div>

        <ManagerSelectField
          value={managerProfileId}
          onChange={(pid) => setManagerProfileId(pid)}
          testIdPrefix="create-session-mgr"
        />

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-[#e3dcd1] text-[#013523] text-sm"
            data-testid="create-session-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#015e3f] text-white text-sm font-medium disabled:opacity-50"
            data-testid="create-session-submit"
          >
            {saving ? 'Creating…' : 'Create session'}
          </button>
        </div>
      </form>
  );

  if (embedded) return body;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="create-session-overlay"
    >
      <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>{body}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">{label}</span>
      {children}
    </label>
  );
}
